import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { readNavigatorLanguage } from '@shared/utils/getNavigatorLanguage';

/**
 * List of country codes that use the imperial system for distance (i.e., miles).
 * These are typically used to infer whether a user prefers miles over kilometers.
 */
export const IMPERIAL_CULTURES = ['US', 'GB', 'LR', 'MM'] as const;

/**
 * Currencies whose associated countries use the imperial system for road distances.
 * Used to derive mi/km from the travel page's default currency setting.
 */
const IMPERIAL_CURRENCIES = new Set(['USD', 'GBP']);

/**
 * Returns true when the given currency code belongs to an imperial-distance country.
 * Falls back to false (metric) for unknown or missing values.
 */
export const isImperialCurrency = (currencyCode: string | null | undefined): boolean => {
  return !!currencyCode && IMPERIAL_CURRENCIES.has(currencyCode);
};

/**
 * Hook to determine whether the current user's locale corresponds to a country
 * that uses the imperial system for measuring distance (e.g., miles).
 *
 * Uses the raw browser region as primary (accurate for all locales including
 * unsupported ones like nl-NL, pt-BR that useGeoBasedLocale normalizes to US),
 * falling back to geo-based culture when unavailable (SSR).
 *
 * @returns `true` if the user's locale region uses miles, otherwise `false`.
 */
export const useIsImperialCultures = (): boolean => {
  const { culture } = useGeoBasedLocale();
  const browserLanguage = readNavigatorLanguage('');
  // eslint-disable-next-line compat/compat
  const browserRegion = browserLanguage ? new Intl.Locale(browserLanguage).region?.toUpperCase() : undefined;
  const countryCode = browserRegion ?? culture;
  return IMPERIAL_CULTURES.includes(countryCode as (typeof IMPERIAL_CULTURES)[number]);
};
